import React, { useState, useEffect } from "react";
import { CurrencyCircleDollar, Clock, ChartLine, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import api from "@/lib/api";
import { Field, Input, Button } from "./UI";

/**
 * Ortak Fiyat Güncelleme Modalı — Stok, Sperma, İlaç için tek bileşen.
 *
 * Props:
 *  - item: { id, name, unit_price, unit? }  — güncellenecek kayıt (name = başlık)
 *  - endpoint: PATCH endpoint (ör: "/semen-stock/{id}/price" veya "/medicines/{id}/price")
 *  - historyEndpoint: GET endpoint (ör: "/semen-stock/{id}/price-history")
 *  - trendEndpoint: GET endpoint (ör: "/semen-stock/{id}/price-trend?months=12") — opsiyonel
 *  - unitLabel: "payet" | "kutu" | "ml" gibi birim etiketi
 *  - title: modal başlığı (default: "Fiyat Güncelle")
 *  - subtitle: modal alt başlığı (default: item.name)
 *  - impactNote: geçmiş kayıtların etkilenmediğine dair açıklama metni (opsiyonel)
 *  - onDone(): başarılı güncelleme sonrası callback (kapat + refresh)
 *  - onClose(): kapatma callback
 */
export default function PriceUpdateModal({
  item,
  endpoint,
  historyEndpoint,
  trendEndpoint,
  unitLabel = "birim",
  title = "Fiyat Güncelle",
  subtitle,
  impactNote,
  readOnly = false,
  onDone,
  onClose,
}) {
  const [newPrice, setNewPrice] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    if (historyEndpoint) {
      api.get(historyEndpoint)
        .then(r => setHistory(r.data.history || []))
        .catch(() => {});
    }
    if (trendEndpoint) {
      api.get(trendEndpoint)
        .then(r => setTrend(r.data))
        .catch(() => {});
    }
  }, [historyEndpoint, trendEndpoint]);

  const priceNum = Number(newPrice || 0);
  const currentPrice = Number(item.unit_price || 0);
  const isSame = Math.abs(priceNum - currentPrice) < 1e-9;
  const priceDelta = priceNum - currentPrice;
  const percentChange = currentPrice > 0 ? (priceDelta / currentPrice) * 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (priceNum <= 0) return toast.error("Geçerli bir fiyat girin");
    if (isSame) return toast.error("Yeni fiyat mevcut fiyatla aynı");
    setSaving(true);
    try {
      const { data } = await api.patch(endpoint, {
        unit_price: priceNum,
        reason: reason || null,
      });
      toast.success(`${item.name} — ${data.old_price.toLocaleString("tr-TR")} ₺ → ${data.new_price.toLocaleString("tr-TR")} ₺`);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="price-update-modal">
      <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#FEF6E0] rounded-full">
              <CurrencyCircleDollar size={22} weight="duotone" className="text-[#8B6914]" />
            </div>
            <div>
              <h3 className="heading text-xl">{title}</h3>
              <p className="text-sm text-[#5e675e]">{subtitle || item.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-[#8a938c] hover:text-[#1A1D1A]" data-testid="price-update-close">×</button>
        </div>

        <div className="bg-[#F7F6F3] border border-[#E5E2DC] rounded-md p-3 mb-4 text-sm">
          <div className="flex justify-between">
            <span className="text-[#5e675e]">Mevcut birim fiyat:</span>
            <span className="tabnum font-semibold" data-testid="price-update-current">
              {currentPrice.toLocaleString("tr-TR")} ₺ / {unitLabel}
            </span>
          </div>
        </div>

        <form onSubmit={submit} className={`space-y-3 ${readOnly ? "hidden" : ""}`}>
          <Field label={`Yeni Birim Fiyat (₺ / ${unitLabel}) *`}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              required
              autoFocus
              data-testid="price-update-input"
              value={newPrice}
              onChange={e => setNewPrice(e.target.value)}
              placeholder="Örn: 650"
            />
          </Field>

          <Field label="Neden (opsiyonel)">
            <Input
              data-testid="price-update-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Örn: Piyasa fiyatı arttı, Yeni tedarikçi, Dolar/TL değişimi"
            />
          </Field>

          {!isSame && priceNum > 0 && currentPrice > 0 && (
            <div className={`text-sm px-3 py-2 rounded-md border ${priceDelta > 0 ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8B3A29]" : "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="price-update-delta">
              {priceDelta > 0 ? "📈" : "📉"} {priceDelta > 0 ? "+" : ""}{priceDelta.toFixed(2)} ₺ · <b>{percentChange > 0 ? "+" : ""}{percentChange.toFixed(1)}%</b> değişim
            </div>
          )}

          <div className="text-xs text-[#5e675e] bg-[#FEF6E0] border border-[#EED7A6] px-3 py-2 rounded-md" data-testid="price-update-warning">
            ✋ <b>Önemli:</b> {impactNote || "Geçmiş kullanım/tüketim finans kayıtları etkilenmez — bu kayıtlar tüketildikleri anki fiyatta donmuş kalır. Sadece bundan sonraki tüketimler yeni fiyattan hesaplanır."}
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>İptal</Button>
            <Button type="submit" disabled={saving || isSame || priceNum <= 0} data-testid="price-update-submit">
              {saving ? "Kaydediliyor..." : "Fiyatı Güncelle"}
            </Button>
          </div>
        </form>

        {/* 12 Aylık Fiyat Trend Grafiği */}
        {trend && trend.series && trend.series.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#E5E2DC]" data-testid="price-update-trend">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ChartLine size={16} weight="duotone" className="text-[#5e675e]" />
                <h4 className="heading text-sm">{trend.series.length} Aylık Fiyat Trendi</h4>
              </div>
              {trend.change_count > 0 && (
                <span className={`text-xs font-semibold flex items-center gap-1 ${trend.stats.change_amount > 0 ? "text-[#8B3A29]" : trend.stats.change_amount < 0 ? "text-[#2D4A36]" : "text-[#5e675e]"}`}>
                  {trend.stats.change_amount > 0 ? <TrendUp size={12} weight="bold" /> : trend.stats.change_amount < 0 ? <TrendDown size={12} weight="bold" /> : <Minus size={12} />}
                  {trend.stats.change_pct > 0 ? "+" : ""}{trend.stats.change_pct}%
                </span>
              )}
            </div>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <LineChart data={trend.series} margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#8a938c" }} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC", fontSize: 12 }}
                    formatter={(v) => [`${Number(v).toLocaleString("tr-TR")} ₺`, "Fiyat"]}
                  />
                  <ReferenceLine y={trend.stats.avg} stroke="#DDA74F" strokeDasharray="3 3"
                    label={{ value: `Ort ${Number(trend.stats.avg).toLocaleString("tr-TR")}`, position: "insideTopLeft", fill: "#8B6914", fontSize: 10 }} />
                  <Line type="stepAfter" dataKey="price" stroke="#2D4A36" strokeWidth={2.5} dot={{ r: 3, fill: "#2D4A36" }} name="Fiyat" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-stat-min">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">En Düşük</div>
                <div className="tabnum font-semibold text-[#2D4A36]">{Number(trend.stats.min).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-stat-max">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">En Yüksek</div>
                <div className="tabnum font-semibold text-[#8B3A29]">{Number(trend.stats.max).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-stat-avg">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">Ortalama</div>
                <div className="tabnum font-semibold text-[#8B6914]">{Number(trend.stats.avg).toLocaleString("tr-TR")} ₺</div>
              </div>
              <div className="p-2 bg-[#F7F6F3] rounded" data-testid="trend-stat-changes">
                <div className="text-[#8a938c] uppercase tracking-widest text-[9px]">Değişim</div>
                <div className="tabnum font-semibold">{trend.change_count} kez</div>
              </div>
            </div>
          </div>
        )}

        {/* Geçmiş */}
        {history.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#E5E2DC]" data-testid="price-update-history">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={14} weight="duotone" className="text-[#5e675e]" />
              <h4 className="heading text-sm">Fiyat Geçmişi ({history.length})</h4>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {history.slice().reverse().map((h, i) => {
                const delta = Number(h.new_price) - Number(h.old_price);
                const upTone = delta > 0 ? "text-[#8B3A29]" : delta < 0 ? "text-[#2D4A36]" : "text-[#5e675e]";
                const d = h.date ? new Date(h.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "";
                return (
                  <div key={`${h.date}-${i}`} className="text-xs bg-[#F7F6F3] px-2 py-1.5 rounded flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="tabnum">
                        <span className="text-[#8a938c]">{Number(h.old_price).toLocaleString("tr-TR")}</span>
                        <span className="mx-1 text-[#8a938c]">→</span>
                        <b>{Number(h.new_price).toLocaleString("tr-TR")}</b>
                        <span className="text-[#5e675e]"> ₺</span>
                        <span className={`ml-2 ${upTone}`}>({delta > 0 ? "+" : ""}{delta.toFixed(2)})</span>
                      </div>
                      {h.reason && <div className="text-[#6b746c] italic truncate">"{h.reason}"</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[#8a938c]">{d}</div>
                      <div className="text-[10px] text-[#8a938c]">{h.changed_by_name || h.changed_by}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
